import React, { useEffect, useState } from "react"

import { useForm } from "react-hook-form"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { TooltipElement } from "@front-shared/elements/tooltip.element"
import type { IStepperStepProps } from "@front-shared/types/props.types"

import { GENERAL_CONFIG } from "../../../../config/general.config"
import { TOOLTIPS_CONFIG } from "../../../../config/tooltips.config"
import { UtilHelper } from "../../../../helpers/util.helper"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { authSelector } from "../../../../store/selectors/auth.selector"
import { authCheckEmailAvailable } from "../../../../store/thunks/auth.thunks"
import { IEmailData } from "../../../../types/data.types"

import arrowRightIcon from "@front-shared/assets/images/icons/arrow-right2.svg"
import mailIcon from "@front-shared/assets/images/icons/mail-dark.svg"
import mainImage from "@front-shared/assets/images/shapes/register.svg"

export const EmailStep: React.FunctionComponent<IStepperStepProps> = props => {
    const dispatch = useAppDispatch()

    const authState = useAppSelector(authSelector)

    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        watch,
    } = useForm<IEmailData | any>()

    const providedEmail = watch("email")

    const [predefinedProcessed, setPredefinedProcessed] =
        useState<boolean>(false)
    const [isEmailDisabled, setIsEmailDisabled] = useState<boolean>(false)

    useEffect(() => {
        if (predefinedProcessed) {
            return
        }

        setPredefinedProcessed(true)

        const predefinedEmail =
            props.defaultValues?.email ||
            UtilHelper.getRawUrlSearchParam(
                GENERAL_CONFIG.urlSearchParamsKeys.email,
            )

        if (predefinedEmail) {
            setValue("email", predefinedEmail)
            // Our current use case for predefined email is for account creation from an invitation link
            // We don't want to allow the user to change the email if it's predefined for an invitation
            setIsEmailDisabled(true)
        }
    }, [props.defaultValues])

    const onSubmit = (data: IEmailData) => {
        dispatch(
            authCheckEmailAvailable({
                payload: data.email,

                onSuccess: () => {
                    props.onStepSubmit({
                        email: providedEmail,
                    } as IEmailData)
                },
            }),
        )
    }

    return (
        <div className="relative">
            <div className="w-full text-center">
                <img
                    src={mainImage}
                    alt="Nirvana"
                    title="Nirvana"
                    className="margin-x-center sm:w-[150px]"
                />
            </div>

            <PrimaryText typography="h1" centered className="mt-[32px]">
                Welcome to Nirvana!
            </PrimaryText>

            <PrimaryText centered typography="subtitle" className="mt-[32px]">
                Insurance coverage solutions optimized for your specialty and
                scale. Creating your account is quick and easy and{" "}
                <br className="sm:hidden" /> should take less than 5 minutes.
            </PrimaryText>

            <PrimaryText
                centered
                typography="subtitle"
                className="mt-[32px] flex items-center justify-center"
            >
                To begin, simply enter your email below:
                <TooltipElement
                    className="ml-[4px]"
                    text={TOOLTIPS_CONFIG.auth.registerEmailCollectionNote}
                />
            </PrimaryText>

            <form onSubmit={handleSubmit(onSubmit)} noValidate={true}>
                <div className="mt-[44px] flex items-center md:block">
                    <InputElement
                        className="mr-[24px] flex-1 md:mr-[0px]"
                        name="email"
                        label="Email address"
                        defaultValue={providedEmail}
                        reactHookControl={control}
                        reactHookErrors={errors}
                        reactHookValidations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                            pattern: FRONT_VALIDATION_CONFIG.email,
                            maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                        }}
                        inputSuffix={
                            <img
                                src={mailIcon}
                                alt="Email address"
                                title="Email address"
                                className="ml-[8px]"
                            />
                        }
                        disabled={isEmailDisabled}
                    />

                    <ButtonElement
                        className="md:mt-[32px] md:text-right"
                        label="Get started"
                        type="primary"
                        htmlType="submit"
                        size="large"
                        icon={arrowRightIcon}
                        isRightIcon
                        isLoading={authState.isLoading}
                    />
                </div>
            </form>
        </div>
    )
}
